"use client";

import { type SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";

import useAuthStore from "@/features/auth/store/useAuthStore";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Email regex pattern (same as in RegisterForm)
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

interface ParentEmailFormProps {
  goToNextStep: () => void;
  goToPrevStep: () => void;
}

const ParentEmailForm = ({ goToNextStep, goToPrevStep }: ParentEmailFormProps) => {
  const { t } = useTranslation(["forms", "auth", "common"]);
  const setOnboardingData = useAuthStore((state) => state.setOnboardingData);
  const parentEmail = useAuthStore((state) => state.onboardingData.parentEmail);

  const parentEmailSchema = z.object({
    email: z
      .string()
      .min(1, t("forms:errors.invalidEmail"))
      .email(t("forms:errors.invalidEmail"))
      .regex(EMAIL_REGEX, t("forms:errors.invalidEmail"))
      .trim(),
  });

  type ParentEmailValues = z.infer<typeof parentEmailSchema>;

  const form = useForm<ParentEmailValues>({
    resolver: zodResolver(parentEmailSchema),
    mode: "onTouched",
    defaultValues: { email: parentEmail ?? "" },
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = form;

  const onSubmit: SubmitHandler<ParentEmailValues> = async ({ email }) => {
    setOnboardingData({ parentEmail: email });
    goToNextStep();
  };

  return (
    <Form {...form}>
      <motion.form
        animate={{ opacity: 1, y: 0 }}
        className="grid w-full gap-6"
        initial={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.5 }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <motion.div animate={{ opacity: 1 }} className="grid gap-4" initial={{ opacity: 0 }}>
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-accent-foreground text-base font-medium">
                  {t("auth:signUp.parentConsent.parentEmail")}
                  <span className="text-destructive ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <motion.div className="relative" whileTap={{ scale: 0.98 }}>
                    <span className="iconify tabler--mail-filled absolute left-2 h-full" />
                    <Input
                      className={`bg-muted/20 border-border/50 hover:bg-muted/30 focus:bg-muted/40 py-0 pl-8 text-sm transition-all duration-200 md:py-6 ${
                        errors.email
                          ? "border-destructive focus-visible:border-secondary focus-visible:ring-destructive"
                          : ""
                      }`}
                      disabled={isSubmitting}
                      placeholder={t("forms:labels.parentEmail")}
                      type="email"
                      {...field}
                    />
                  </motion.div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid gap-4">
            <div className={"flex w-full gap-2"}>
              <Button
                className={
                  "bg-muted/20 hover:bg-muted/30 border-border/50 w-20 shadow-lg backdrop-blur-sm"
                }
                disabled={isSubmitting}
                size="lg"
                type="button"
                variant="outline"
                onClick={goToPrevStep}
              >
                <Icon icon={"mdi:arrow-left"} />
                {t("common:back")}
              </Button>
              <Button
                className={
                  "bg-accent-foreground/90 hover:bg-accent-foreground text-background w-full shadow-xl"
                }
                disabled={isSubmitting || !form.formState.isValid}
                size="lg"
                type="submit"
              >
                {t("common:continue")}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.form>
    </Form>
  );
};

export default ParentEmailForm;
